import {
  useTryDownloadAssetCollectionMutation,
  useGetAssetCollectionStatusMutation,
  useGenerateAssetCollectionDownloadUrlMutation,
  LarkiErrorResponse,
} from '@/redux/rtk';
import { usePoll } from './usePoll';
import { useMemo, useRef, useState } from 'react';
import { ZipJobStatus } from '@larki/shared/type/zipper';
import { downloadFile } from '@/utilities/files';
import { useDispatch } from 'react-redux';
import { alertError, alertWarning } from '@/redux/actions';

export const useDownloadAll = ({ projectId }: { projectId: number }) => {
  const dispatch = useDispatch();
  const [tryDownload] = useTryDownloadAssetCollectionMutation();
  const [getStatus] = useGetAssetCollectionStatusMutation();
  const [generateDownloadUrl] = useGenerateAssetCollectionDownloadUrlMutation();
  const jobRef = useRef<{
    zipId: string;
    taskArn: string;
    clusterArn: string;
  }>(null);
  const [zipProgress, setZipProgress] = useState<number | null>(null);
  const [zipLoading, setZipLoading] = useState(false);

  const { poll } = usePoll({
    // @ts-expect-error untyped
    callback: async (stop) => {
      let status: ZipJobStatus;
      let zipId: string;
      setZipLoading(true);
      if (jobRef.current) {
        const data = await getStatus(jobRef.current).unwrap();
        status = data.status;
        zipId = jobRef.current.zipId;
      } else {
        try {
          const data = await tryDownload({ projectId }).unwrap();
          status = data.status;
          jobRef.current = data.job;
          zipId = data.job.zipId;
        } catch (error) {
          const errorMessage =
            (error as LarkiErrorResponse).data?.message ??
            'Error downloading assets';
          const func =
            (error as LarkiErrorResponse).status === 400
              ? alertWarning
              : alertError;
          dispatch(func(errorMessage));
          setZipLoading(false);
          stop();
          return;
        }
      }

      switch (status.status) {
        case 'starting':
          setZipProgress(0);
          break;
        case 'running':
          setZipProgress(status.progress);
          break;
        case 'completed':
          setZipLoading(false);
          setZipProgress(null);
          jobRef.current = null;
          const { url } = await generateDownloadUrl({
            zipId,
            zipFileName: `zip-${projectId}.zip`,
          }).unwrap();
          await downloadFile(url);
          stop();
          break;
        case 'failed':
          console.error(status.message);
          setZipLoading(false);
          setZipProgress(null);
          jobRef.current = null;
          stop();
          break;
      }
    },
    // @ts-expect-error untyped
    dependencies: [projectId],
    delay: 5000,
  });

  const startDownload = () => {
    poll(true);
  };

  const cancelDownload = () => {
    poll(false);
    setZipLoading(false);
    setZipProgress(null);
    jobRef.current = null;
  };

  return useMemo(
    () => ({
      startDownload,
      cancelDownload,
      zipProgress,
      zipLoading,
    }),
    [zipProgress, zipLoading, startDownload, cancelDownload]
  );
};
